import {
  Box, Flex, IconButton, Select, Text, Tooltip, useBreakpointValue, useColorModeValue,
} from "@chakra-ui/react";
import {FiMic, FiPause, FiSquare, FiBookmark, FiPlay} from "react-icons/fi";
import {useEffect, useRef, useState} from "react";
import {getElapsedSecondsSinceIso} from "../../../../services/helper/general";
import {is} from "date-fns/locale";

const AudioRecorderBar = ({
                            microphones = [],
                            selectedMic,
                            onSelectMic,
                            onPause,
                            onStop,
                            isRecording,
                            isPaused,
                            isStopped,
                            mediaStream,
                            conversation,
                          }) => {
  const [internalTime, setInternalTime] = useState(0);
  const [volumeData, setVolumeData] = useState(new Array(12).fill(0));
  const bgWaveBox = useColorModeValue("red.50", "blue.800");
  const bgMainComponent = useColorModeValue("white", "gray.700");
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (conversation?.createdAt !== undefined) {
        const calculatedElapsedSeconds = getElapsedSecondsSinceIso(conversation.createdAt);
        setElapsedTime(calculatedElapsedSeconds);
      }
    })
  }, [1000, conversation]);

  // Timer logic
  useEffect(() => {
    let interval;
    if (isRecording && !isPaused && conversation !== undefined) {
      interval = setInterval(() => setInternalTime((t) => t + 1), 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  const formatTime = (s) => new Date(s * 1000).toISOString().substr(11, 8);

  useEffect(() => {
    if (!mediaStream) return;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(mediaStream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    source.connect(analyser);

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);
      const avg = dataArray.reduce((sum, val) => sum + Math.abs(val - 128), 0) / bufferLength;

      const totalBars = 16;
      const activeBars = Math.min(totalBars, Math.floor((avg / 20) * totalBars));

      const bars = Array.from({length: totalBars}, (_, i) => i < activeBars ? "active" : "inactive");

      setVolumeData(bars);
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      audioCtx.close();
    };
  }, [mediaStream]);

  const timerFontSize = useBreakpointValue({base: "sm", md: "md"});

  return (
    <Flex
      wrap="wrap"
      align="center"
      justify="space-between"
      p={3}
      bg={bgMainComponent}
      borderRadius="xl"
      shadow="md"
      width="100%"
      maxW="800px"
      mx="auto"
      gap={4}
    >
      <Flex align="center" gap={2}>
        <FiMic/>
        <Select
          size="sm"
          fontSize={"xs"}
          width="200px"
          value={selectedMic}
          onChange={(e) => onSelectMic?.(e.target.value)}
        >
          {microphones.map((microphone) => (
            <option key={microphone.deviceId} value={microphone.deviceId}>
              {microphone.label || "Unknown"}
            </option>
          ))}
        </Select>
      </Flex>

      <Flex
        flex="1"
        bg={bgWaveBox}
        align="center"
        justify="center"
        borderRadius="full"
        px={4}
        py={2}
        minW="200px"
        maxW="400px"
        position="relative"
      >
        <Flex gap={1} align="left">
          {volumeData.map((state, i) => (<Box
            key={i}
            w="4px"
            h="12px"
            bg={state === "active" ? "red.500" : "gray.300"}
            borderRadius="sm"
            transition="background 0.1s"
          />))}
        </Flex>
        <Text
          position="absolute"
          right="40px"
          fontWeight="bold"
          color="gray.500"
          fontSize={timerFontSize}
          display={{base: "none", md: "block"}}
        >
          {formatTime(elapsedTime ?? internalTime)}
        </Text>
        <Tooltip label={isPaused ? 'Hervatten' : 'Pauzeren'} bg={useColorModeValue("orange.100", "blue.800")}
                 size={"lg"}
                 textColor={useColorModeValue("gray.900", "white")} openDelay={1000}>
          <IconButton
            icon={isPaused ? <FiPlay/> : <FiPause/>}
            bg={useColorModeValue("orange.100", "blue.800")}
            size="sm"
            isRound
            aria-label="Pause"
            position="absolute"
            right="0px"
            onClick={onPause}
            transition="all 0.2s ease-in-out"
            _hover={{
              transform: "scale(1.25)",
              boxShadow: "md",
            }}
          />
        </Tooltip>
      </Flex>
      <Tooltip label='Stoppen' bg={"red.200"} textColor={"white"} openDelay={1000}>
        <IconButton
          icon={<FiSquare/>}
          isLoading={isStopped}
          size="sm"
          aria-label="Stop"
          onClick={onStop}
          borderRadius="full"
          border="2px solid"
          borderColor="red.200"
          bg={"red.200"}
          color={"white"}
          transition="all 0.2s ease-in-out"
          _hover={{
            transform: "scale(1.25)",
            boxShadow: "md"
          }}
        />
      </Tooltip>
    </Flex>);
};

export default AudioRecorderBar;
